import React from 'react';

type UseStepperOptions = {
  steps: number;
  isPreFilled?: boolean;
};

type StepMetadata = {
  active: boolean;
  error: boolean;
  complete: boolean;
};

export type GoToRequestedStepFn = (
  predicate: (() => Promise<boolean>) | boolean,
  onSuccess?: () => any
) => unknown;

export const useStepper = ({
  isPreFilled = false,
  steps,
}: UseStepperOptions) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [requestedStep, setRequestedStep] = React.useState(activeStep);
  const lastActiveStep = useLastActiveStep(isPreFilled ? steps : activeStep);

  const [stepsInfo, setStepsInfo] = React.useState(() =>
    initializeStepsInfo(steps));

  /** Set some meta-data about a step. Only used internally. */
  const setStepInfo = (
    step: number,
    key: keyof StepMetadata,
    value: boolean,
  ) => {
    setStepsInfo((steps) =>
      steps.map((s, index) => {
        if (index !== step) return s;
        return {
          ...s,
          [key]: value,
        };
      }));
  };

  /** Get some meta-data about a step. */
  const getStepInfo = React.useCallback(
    (step: number): StepMetadata => {
      if (step < 0 || step > steps) {
        return {
          error: false,
          complete: false,
          active: false,
        };
      }

      return stepsInfo[step];
    },
    // No need to include `stepsInfo` as its an object and
    // the initial closure reference that `getStepInfo` gets
    // is sufficient for future accesses into `stepsInfo`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [steps, stepsInfo],
  );

  /** Unconditionally move forward one step. */
  const nextStep = React.useCallback(() => {
    setActiveStep((s) => {
      if (s >= steps || getStepInfo(s).error) return s;

      setRequestedStep(s + 1);
      setStepInfo(s, 'error', false);
      return s + 1;
    });
    // eslint-disable-next-line
  }, [steps]);

  /** Unconditionally move back one step. */
  const previousStep = React.useCallback(() => {
    setActiveStep((s) => {
      if (s <= 0) return s;

      setRequestedStep(s - 1);
      return s <= 0 ? s : s - 1;
    });
  }, []);

  /** Unconditionally advance to a particular step. */
  const gotoStep = React.useCallback(
    (step: number) => {
      if (step >= steps || step < 0) return;
      setActiveStep(step);
    },
    [steps],
  );

  /** This is useful for advancing the stepper after running some kind of validation
   * function which resolves *later* to a boolean (it passed or it didn't).
   *
   * A nice extra of this function is that it will set the step you are on
   * when running the validation function to "error", if the validation fails.
   * */
  const goToRequestedStep = React.useCallback(
    async (
      predicate: (() => Promise<boolean>) | boolean,
      onSuccess?: () => any,
    ) => {
      let isSuccess: boolean;
      if (requestedStep === activeStep) {
        return;
      }

      if (typeof predicate === 'function') {
        isSuccess = await predicate();
      } else {
        isSuccess = predicate;
      }

      if (isSuccess) {
        if (onSuccess) {
          onSuccess();
        }

        gotoStep(requestedStep);
        setStepInfo(activeStep, 'error', false);
      } else {
        setRequestedStep(activeStep);
        setStepInfo(activeStep, 'error', true);
      }
    },
    [activeStep, gotoStep, requestedStep],
  );

  // eslint-disable-next-line camelcase
  const safe_setRequestedStep = (step: number) => {
    if (step < 0 || step > steps) {
      return;
    }

    setRequestedStep(step);
  };

  return {
    nextStep,
    previousStep,
    gotoStep,
    activeStep,
    lastActiveStep,
    goToRequestedStep,
    // eslint-disable-next-line camelcase
    setRequestedStep: safe_setRequestedStep as typeof setRequestedStep,
    getStepInfo,
  };
};

const useLastActiveStep = (currentActiveStep: number) => {
  const [lastActiveStep, setLastActiveStep] = React.useState(0);

  React.useEffect(() => {
    if (currentActiveStep > lastActiveStep) {
      setLastActiveStep(currentActiveStep);
    }
  }, [currentActiveStep, lastActiveStep]);

  return lastActiveStep;
};

const initializeStepsInfo = (steps: number) => {
  const stepInfo: StepMetadata = {
    active: false,
    complete: false,
    error: false,
  };

  const result: StepMetadata[] = [];
  for (let i = 0; i < steps; i++) {
    result[i] = stepInfo;
  }

  return result;
};
